import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccommodationType } from '@aviv_price-pages/brand';
import { useLocation } from '@aviv-unified-frontend/runtime/router';
import { Box } from '@gemini/core';
import { PageSpinner } from '@/components/PageSpinner';
import ApiError from '@/entities/errors/ApiError';
import InactivePlaceError from '@/entities/errors/InactivePlaceError';
import PlaceNotFoundError from '@/entities/errors/PlaceNotFoundError';
import { HttpError } from '@/entities/types/HttpError';
import { useClientSideRedirect } from '@/hooks/useClientSideRedirect/useClientSideRedirect';
import { useGetPlaceData } from '@/hooks/useGetPlaceData';
import { Logger } from '@/logger';
import { useScrollToTop } from '../useScrollToTop/useScrollToTop';
export const PageLayoutContext = createContext({});
/**
 * Provider for the page layout context, it should wrap every childs of the PageLayout component
 *
 * @param props Page props
 * @returns The layout wrapper
 */
export const PageLayoutContextProvider = ({ children }) => {
    useClientSideRedirect();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [accommodationType, setAccommodationType] = useState(AccommodationType.APARTMENT);
    const { pathname } = useLocation();
    const { data, isLoading, error } = useGetPlaceData(pathname);
    const resolvedData = data ?? null;
    const resolvedError = error;
    if (!isLoading && resolvedError) {
        if (resolvedData && !resolvedData.pricePageContext?.place.active) {
            throw new InactivePlaceError(resolvedData.pricePageContext.place);
        }
        if (resolvedError instanceof PlaceNotFoundError) {
            throw new PlaceNotFoundError(pathname, resolvedError);
        }
        if (resolvedError instanceof HttpError) {
            const status = resolvedError.getStatusCode();
            if (status >= 500) {
                Logger.error('Get data raised an error', { error: resolvedError });
                throw new ApiError('500 error');
            }
            if (status === 404) {
                throw new PlaceNotFoundError(pathname, resolvedError);
            }
            throw new HttpError({ status });
        }
        throw resolvedError;
    }
    useEffect(() => {
        if (!sidebarOpen) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [sidebarOpen]);
    useScrollToTop(resolvedData);
    const previouslyDefinedContext = useRef(null);
    const context = useMemo(() => {
        /**
         * Previously, a null value was returned, which triggered the PageSpinner display (see `if (!context)` condition below).
         * Returning null directly is unsafe when a network request is in progress, especially without verifying the data and loading states.
         * This issue can occur after a user interacts with the map, which updates the pathname and causes the fetcher key to re-trigger a fetch: resolvedData will be then initially undefined and so the context sets to null.
         * The solution implemented here aims to keep the previously defined context if `resolvedData` is not yet fetched.
         */
        if (!resolvedData)
            return previouslyDefinedContext.current;
        const context = {
            sidebarOpen,
            setSidebarOpen,
            accommodationType,
            setAccommodationType,
            pricePageProps: resolvedData,
            isLoading,
        };
        previouslyDefinedContext.current = context;
        return context;
    }, [sidebarOpen, accommodationType, resolvedData, isLoading]);
    if (!context) {
        return (_jsx(Box, { position: "absolute", top: "0", bottom: "0", left: "0", right: "0", backgroundColor: "color.background.light", zIndex: "zIndex.3", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", opacity: "opacity.80", children: _jsx(PageSpinner, { flexDirection: "row" }) }));
    }
    return _jsx(PageLayoutContext.Provider, { value: context, children: children });
};
/**
 * Hook to get the page layout context.
 * Must be used within a <PageLayoutContextProvider>.
 *
 * @returns Page layout state and callbacks
 */
export const usePageLayoutContext = () => {
    const context = useContext(PageLayoutContext);
    if (!context || Object.keys(context).length === 0) {
        throw new Error('usePageLayoutContext must be used within a PageLayoutContextProvider');
    }
    return context;
};
